<template>
    <div class="layout">
        <aside
            class="sidebar"
            :class="{ collapsed: isCollapsed }"
        >
            <div
                class="sidebar-scroll"
                @scroll="closeOpenedMenu"
            >
                <ul>
                    <!-- Expandir/Recolher -->
                    <li @click="toggleMenu">
                        <img
                            :src="menuIcon"
                            alt="Toggle Menu"
                        />

                        <span>
                            {{ isCollapsed ? 'Expandir' : 'Recolher' }}
                        </span>
                    </li>


                    <!-- Menus -->
                    <li
                        v-for="menu in menusTree"
                        :key="menu.id"
                        class="menu-item"
                        @mouseenter="openMenu(menu, $event)"
                        @mouseleave="scheduleCloseMenu(menu)"
                        @click="handleMenuClick(menu)"
                    >
                        <img
                            :src="getIcon(menu.icone)"
                            :alt="menu.titulo_menu"
                        />

                        <span>{{ menu.titulo_menu }}</span>


                        <!-- Submenu -->
                        <div
                            v-if="menu.tipo === 'GRUPO' && isMenuOpen(menu)"
                            ref="submenuRef"
                            class="submenu"
                            :style="submenuStyle"
                            @mouseenter="cancelCloseMenu"
                            @mouseleave="scheduleCloseMenu(menu)"
                        >
                            <ul>
                                <li
                                    v-for="filho in menu.filhos"
                                    :key="filho.id"
                                    @click.stop="selectMenuItem(filho)"
                                >
                                    <span>{{ filho.titulo_menu }}</span>
                                </li>
                            </ul>
                        </div>
                    </li>


                    <!-- Logout -->
                    <li
                        class="logout"
                        @click="confirmLogout"
                    >
                        <img
                            :src="getIcon('menuLogout')"
                            alt="Logout"
                        />

                        <span>Desconectar</span>
                    </li>
                </ul>
            </div>
        </aside>


        <main class="content">
            <header class="header">
                <div class="header-title">
                    {{ nmTitulo || globalStore.nmperfil }}
                </div>

                <div class="header-user-area">
                    <div class="user-info">
                        <div class="user-name">
                            {{ globalStore.nmusuario }}
                        </div>

                        <div class="establishment-name">
                            {{
                                globalStore.nmestabelecimento ||
                                globalStore.nmsim ||
                                globalStore.nmep
                            }}
                        </div>
                    </div>

                    <div class="token-info">
                        {{ timeRemaining }}
                    </div>
                </div>
            </header>


            <div class="main-content">
                <slot>
                    <component
                        :is="activeComponent"
                        :key="activeComponentKey"
                        v-bind="{
                            ...activeComponentProps,
                            ...activeComponentEvents
                        }"
                        @close="closeActiveComponent"
                    />
                </slot>
            </div>
        </main>
    </div>
</template>


<script setup>
import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref
} from 'vue';

import { useRouter } from 'vue-router';
import { jwtDecode } from 'jwt-decode';

import { Api } from '@/funcoes/apiExport';
import { componentRegistry } from '@/config/menuComponentRegistry';
import { iconRegistry } from '@/config/menuIconRegistry';
import { globalStore } from '@/globals';


/* =========================================================
   VARIÁVEIS GERAIS
   ========================================================= */

const router = useRouter();

const pilhaNavegacao = ref([]);

const isCollapsed = ref(true);
const nmTitulo = ref(null);
const menuAtivo = ref(null);
const menusApi = ref([]);

const openedGroupId = ref(null);

const timeRemaining = ref(null);

let timer = null;


/* =========================================================
   SUBMENU
   ========================================================= */

const submenuRef = ref(null);

const submenuTop = ref(0);
const submenuLeft = ref(0);

let closeMenuTimer = null;


const submenuStyle = computed(() => ({
    top: `${submenuTop.value}px`,
    left: `${submenuLeft.value}px`
}));


function cancelCloseMenu() {
    if (!closeMenuTimer) return;

    clearTimeout(closeMenuTimer);
    closeMenuTimer = null;
}


async function openMenu(menu, event) {
    if (menu.tipo !== 'GRUPO') return;

    cancelCloseMenu();

    const elemento = event.currentTarget;
    const rect = elemento.getBoundingClientRect();

    /*
       Sobreposição de alguns pixels para eliminar
       o espaço entre a scrollbar e o submenu.
    */
    submenuLeft.value = rect.right - 5;
    submenuTop.value = rect.top;

    openedGroupId.value = menu.id;

    await nextTick();

    ajustarSubmenuNaTela();
}


function ajustarSubmenuNaTela() {
    const elemento = Array.isArray(submenuRef.value)
        ? submenuRef.value[0]
        : submenuRef.value;

    if (!elemento) return;

    const rect = elemento.getBoundingClientRect();
    const margem = 6;

    /*
       Caso o submenu ultrapasse a parte inferior da tela,
       desloca-o para cima.
    */
    if (rect.bottom > window.innerHeight - margem) {
        submenuTop.value = Math.max(
            margem,
            submenuTop.value - (
                rect.bottom -
                window.innerHeight +
                margem
            )
        );
    }

    /*
       Proteção para não ultrapassar o topo.
    */
    if (submenuTop.value < margem) {
        submenuTop.value = margem;
    }
}


function scheduleCloseMenu(menu) {
    cancelCloseMenu();

    closeMenuTimer = setTimeout(() => {
        if (
            menu.tipo === 'GRUPO' &&
            openedGroupId.value === menu.id
        ) {
            openedGroupId.value = null;
        }

        closeMenuTimer = null;
    }, 200);
}


function closeOpenedMenu() {
    cancelCloseMenu();
    openedGroupId.value = null;
}


const isMenuOpen = (menu) => {
    return openedGroupId.value === menu.id;
};


/* =========================================================
   ÍCONES
   ========================================================= */

const menuIcon = computed(() => {
    return isCollapsed.value
        ? iconRegistry.menuExpand
        : iconRegistry.menuCollapse;
});


function getIcon(icone) {
    if (!icone) return iconRegistry.default;

    return iconRegistry[icone] || iconRegistry.default;
}


/* =========================================================
   NORMALIZAÇÃO DO MENU
   ========================================================= */

const menusTree = computed(() => {
    const ativos = (menusApi.value || [])
        .filter(item => item.ativo === 'S')
        .sort(
            (a, b) =>
                (a.ordem || 0) -
                (b.ordem || 0)
        );


    const grupos = ativos
        .filter(item => item.tipo === 'GRUPO')
        .map(grupo => ({
            ...grupo,

            filhos: ativos
                .filter(item =>
                    item.idmenu_pai === grupo.id &&
                    item.tipo === 'ITEM'
                )
                .sort(
                    (a, b) =>
                        (a.ordem || 0) -
                        (b.ordem || 0)
                )
        }));


    const itensRaiz = ativos.filter(item =>
        item.tipo === 'ITEM' &&
        (
            item.idmenu_pai === null ||
            item.idmenu_pai === undefined
        )
    );


    return [...grupos, ...itensRaiz]
        .sort(
            (a, b) =>
                (a.ordem || 0) -
                (b.ordem || 0)
        );
});


/* =========================================================
   COMPONENTE ATIVO
   ========================================================= */

const activeComponent = computed(() => {
    const nome = menuAtivo.value?.componente;

    if (
        nome &&
        componentRegistry[nome]
    ) {
        return componentRegistry[nome];
    }

    return globalStore.showAcoesEstabelecimento
        ? componentRegistry.EstabelecimentoDashPage
        : componentRegistry.ResumoGeralPage;
});


const activeComponentKey = computed(() => {
    const menu = menuAtivo.value;

    if (!menu) {
        return globalStore.showAcoesEstabelecimento
            ? 'EstabelecimentoDashPage'
            : 'ResumoGeralPage';
    }

    return `${menu.id}-${menu.componente}`;
});


const activeComponentProps = computed(() => {
    const menu = menuAtivo.value;

    if (!menu?.props) return {};

    return parseMenuProps(menu.props);
});


const activeComponentEvents = computed(() => {
    const nome = menuAtivo.value?.componente;

    if (nome === 'EstabelecimentosRtTabPage') {
        return {
            onSimAlterado: handleSimAlterado
        };
    }

    return {};
});


function parseMenuProps(props) {
    if (!props) return {};

    if (typeof props === 'object') {
        return props;
    }

    try {
        return JSON.parse(props);
    } catch (error) {
        console.error('Props inválidas:', props, error);
        return {};
    }
}


/* =========================================================
   AÇÕES DO MENU
   ========================================================= */

function toggleMenu() {
    closeOpenedMenu();

    isCollapsed.value = !isCollapsed.value;
}


function handleMenuClick(menu) {
    if (menu.tipo === 'ITEM') {
        selectMenuItem(menu);
    }
}


function selectMenuItem(
    item,
    registrarHistorico = true
) {
    cancelCloseMenu();

    if (registrarHistorico) {
        pilhaNavegacao.value = [];

        window.history.pushState(
            {
                internal: true,
                menuId: item.id
            },
            ''
        );
    }

    menuAtivo.value = item;

    nmTitulo.value =
        item.titulo_pagina ||
        item.titulo_menu ||
        null;

    openedGroupId.value = null;
}


function closeActiveComponent() {
    voltarInterno();
}


/* =========================================================
   API DE MENUS
   ========================================================= */

async function carregarMenus() {
    try {
        const resposta = await Api.genericPost(
            '/main/usuario/menus',
            {
                idusuario: globalStore.idusuario,
                idsim: globalStore.idsim
            }
        );

        menusApi.value = Array.isArray(resposta)
            ? resposta
            : (resposta.lsMenus || []);

    } catch (error) {
        console.error(
            'Erro ao carregar menus:',
            error
        );

        menusApi.value = [];
    }
}


async function handleSimAlterado() {
    await carregarMenus();

    menuAtivo.value = null;
    nmTitulo.value = null;

    closeOpenedMenu();
}


/* =========================================================
   LOGIN
   ========================================================= */

function confirmLogout() {
    resetLogin();
}


function resetLogin() {
    closeOpenedMenu();

    Api.logoutAPI();

    router.push('/login');
}


/* =========================================================
   CONTROLE DO TOKEN
   ========================================================= */

function updateTimeRemaining() {
    const token = localStorage.getItem('jwtToken');

    if (!token) {
        timeRemaining.value = 'Token não encontrado';
        clearInterval(timer);
        return;
    }

    try {
        const decodedToken = jwtDecode(token);

        const expiration = decodedToken.exp * 1000;
        const now = Date.now();

        const difference = expiration - now;

        if (difference <= 0) {
            timeRemaining.value = 'Token expirado';
            clearInterval(timer);
            return;
        }

        const hours = Math.floor(
            difference / (1000 * 60 * 60)
        );

        const minutes = Math.floor(
            (difference / (1000 * 60)) % 60
        );

        const seconds = Math.floor(
            (difference / 1000) % 60
        );

        timeRemaining.value = [
            hours.toString().padStart(2, '0'),
            minutes.toString().padStart(2, '0'),
            seconds.toString().padStart(2, '0')
        ].join(':');

    } catch (error) {
        console.error(
            'Erro ao decodificar o token:',
            error
        );

        timeRemaining.value =
            'Erro ao verificar o token';

        clearInterval(timer);
    }
}


/* =========================================================
   NAVEGAÇÃO INTERNA
   ========================================================= */

function voltarInterno() {
    closeOpenedMenu();

    if (pilhaNavegacao.value.length > 0) {
        const anterior = pilhaNavegacao.value.pop();

        menuAtivo.value = anterior.menu;
        nmTitulo.value = anterior.titulo;

        return true;
    }

    if (menuAtivo.value) {
        menuAtivo.value = null;
        nmTitulo.value = null;

        pilhaNavegacao.value = [];

        return true;
    }

    return false;
}


function onBrowserBack() {
    const voltou = voltarInterno();

    if (!voltou) {
        window.history.pushState(
            { internal: true },
            ''
        );
    }
}


/* =========================================================
   CICLO DE VIDA
   ========================================================= */

onMounted(async () => {
    window.history.replaceState(
        { internal: true },
        ''
    );

    await carregarMenus();

    updateTimeRemaining();

    timer = setInterval(
        updateTimeRemaining,
        1000
    );

    window.addEventListener(
        'popstate',
        onBrowserBack
    );
});


onBeforeUnmount(() => {
    clearInterval(timer);

    cancelCloseMenu();

    window.removeEventListener(
        'popstate',
        onBrowserBack
    );
});
</script>